import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, resource, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OrganizationUsage } from '@sona/shared-model-common';
import { SseClient } from 'ngx-sse-client';
import { firstValueFrom, map } from 'rxjs';

/**
 * A facade for the general authentication
 */
@Injectable({
    providedIn: 'root'
})
export class SonaUsageFacade implements OnDestroy {

    private readonly _sseClient = inject(SseClient);
    private readonly _httpClient = inject(HttpClient);
    private readonly _usageStream = this._sseClient.stream(`/api/organizations/usage/changes`, {
        keepAlive: true,
        reconnectionDelay: 1000
    }, {}, 'GET').pipe(
        map((event) => JSON.parse((event as MessageEvent<string>).data) as OrganizationUsage)
    );
    private readonly _usageResource = resource({
        loader: () => firstValueFrom(this._httpClient.get<OrganizationUsage>(`/api/organizations/usage`))
    });
    readonly usageResource = this._usageResource.asReadonly();
    readonly usage = this._usageResource.value.asReadonly();

    readonly isUsageLimitReached = computed(() => {
        const usage = this.usage();

        if (usage == null) {
            return undefined;
        }

        const credits = usage.subscription.credits ?? 0;
        return credits === -1 ? false : (usage.creditsUsed ?? 0) >= credits;
    });

    readonly usageReloadInterval = setInterval(() => {
        this._usageResource.reload();
    }, 60000);

    get isLoading() {
        return this._usageResource.isLoading;
    }

    ngOnDestroy() {
        clearInterval(this.usageReloadInterval);
    }

    updateUsage() {
        this._usageResource.reload();
    }
}
